import {
  Container,
  Dimmer,
  Header,
  Icon,
  Loader,
  Segment,
  Sidebar,
  SidebarPushable,
  SidebarPusher,
} from '@mairistem/composants';
import { newIdentifiant, request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { useHistory, useParams } from 'react-router';

import { TileList } from '@/components/Autre/Tile';
import { MENUS } from '@/constants';
import { RegieContext } from '@/contexts';
import type { RegieContextType } from '@/contexts/RegieContext';
import { baseUrlFront, getRegies, getUtilisateurRegies } from '@/routes';
import type { ApiResponseType, RegieType } from '@/types';
import { checkRegieLimit, estEncaissementDirect, getInfosEncaissementDirect } from '@/utils';
import { useRegieStore, useRegisseurStore, useUtilisateurStore } from '@/zustand';

const RegieContextProvider = ({ children }: { children: React.ReactNode }) => {
  /** Utilisateur connecté */
  const utilisateur = useUtilisateurStore((state) => state.utilisateur);
  /** S'il y a des régies créé en BDD */
  const hasRegies = useRegieStore((state) => state.hasRegies);
  /** Régie sélectionnée */
  const selectedRegieIdentifiant = useRegieStore((state) => state.regie?.identifiant);
  const setRegie = useRegieStore((state) => state.setRegie);
  const updateRegie = useRegieStore((state) => state.updateRegie);
  /** Regisseur connecté */
  const disconnect = useRegisseurStore((state) => state.disconnect);

  /** Ref pour éviter les appels API multiples */
  const hasLoadedRegiesRef = React.useRef(false);

  const [forceSidebarVisible, setForceSidebarVisible] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(_.isNil(selectedRegieIdentifiant));
  const [regies, setRegies] = React.useState<RegieType[]>();
  const [unknownUser, setUnknownUser] = React.useState<boolean>(false);
  const [cannotCreateRegie, setCannotCreateRegie] = React.useState<boolean>(false);

  const history = useHistory();
  const params = useParams<Record<string, string>>();
  const { identifiant } = params || {};

  const showList = React.useCallback(() => {
    setForceSidebarVisible(true);
  }, []);

  const handleRegieSelect = React.useCallback(
    (reg: RegieType, updateOnly: boolean = false) => {
      if (updateOnly) {
        updateRegie({
          identifiant: reg.identifiant,
          titre: reg.titre,
          codeInterne: reg.codeInterne,
          regisseurConnexion: reg.regisseurConnexion,
          ecole: reg.ecole,
          autonome: reg.autonome,
          dateArrete: reg.dateArrete,
          dateFinDernierArrete: reg.dateFinDernierArrete,
          typeDernierArrete: reg.typeDernierArrete,
          dateProduction: reg.dateProduction,
          nombreReglementsAVerser: reg.nombreReglementsAVerser,
          periodiciteArrete: reg.periodiciteArrete,
          periodiciteVersementEncaisseGlobale: reg.periodiciteVersementEncaisseGlobale,
          regieProlongee: reg.regieProlongee,
          joursEncaissementSupplementaires: reg.joursEncaissementSupplementaires,
          encaissementPossible: reg.encaissementPossible,
          annulationPossible: reg.annulationPossible,
          arreteAnnuelRequis: reg.arreteAnnuelRequis,
          arreteAnnuelCas: reg.arreteAnnuelCas,
          arreteAnnuelAnnee: reg.arreteAnnuelAnnee,
          arreteConfig: reg.arreteConfig,
        });
      } else {
        if (reg) {
          setRegie({
            identifiant: reg.identifiant,
            titre: reg.titre,
            codeInterne: reg.codeInterne,
            regisseurConnexion: reg.regisseurConnexion,
            ecole: reg.ecole,
            autonome: reg.autonome,
            dateArrete: reg.dateArrete,
            dateFinDernierArrete: reg.dateFinDernierArrete,
            typeDernierArrete: reg.typeDernierArrete,
            dateProduction: reg.dateProduction,
            nombreReglementsAVerser: reg.nombreReglementsAVerser,
            periodiciteArrete: reg.periodiciteArrete,
            periodiciteVersementEncaisseGlobale: reg.periodiciteVersementEncaisseGlobale,
            regieProlongee: reg.regieProlongee,
            joursEncaissementSupplementaires: reg.joursEncaissementSupplementaires,
            encaissementPossible: reg.encaissementPossible,
            annulationPossible: reg.annulationPossible,
            arreteAnnuelRequis: reg.arreteAnnuelRequis,
            arreteAnnuelCas: reg.arreteAnnuelCas,
            arreteAnnuelAnnee: reg.arreteAnnuelAnnee,
            arreteConfig: reg.arreteConfig,
          });
        }
        // Si on se positionne sur une régie différente
        // et que la régie a activé la double authentification
        if (reg.identifiant !== selectedRegieIdentifiant && reg.regisseurConnexion) {
          // On "déco" le régisseur
          disconnect();
        }

        setForceSidebarVisible(false);
      }
    },
    [disconnect, selectedRegieIdentifiant, setRegie, updateRegie],
  );

  // Ajoute le header X-Regie aux requêtes
  React.useEffect(() => {
    const xRegie = selectedRegieIdentifiant;

    const interceptor = request.interceptors.request.use((config) => {
      config.headers['X-Regie'] = xRegie;

      return config;
    });

    return () => {
      request.interceptors.request.eject(interceptor);
    };
  }, [selectedRegieIdentifiant]);

  React.useEffect(() => {
    // Éviter les appels API multiples
    if (hasLoadedRegiesRef.current) {
      return undefined;
    }

    let cancelled = false;

    if (!_.isNil(utilisateur)) {
      // Si aucune régie en BDD, on vérifie les droits de création
      // avant de rediriger l'utilisateur vers la création de régie
      if (hasRegies === false) {
        hasLoadedRegiesRef.current = true;

        // En encaissement direct, pas de redirection vers la création de régie
        if (estEncaissementDirect()) {
          setUnknownUser(true);
          setLoading(false);

          return undefined;
        }

        checkRegieLimit()
          .then((response) => {
            if (cancelled) return;

            const { facturier, autonome } = response;

            if (!facturier.canCreate && !autonome.canCreate) {
              // Aucun droit de création → message pleine page
              setCannotCreateRegie(true);
              setLoading(false);
            } else if (_.isNil(identifiant)) {
              // Droits OK, pas encore sur la route de création → rediriger
              history.push(`${baseUrlFront}/constitution/${newIdentifiant()}`);
            } else {
              // Droits OK, déjà sur la route de création
              setRegies([]);
              setLoading(false);
            }
          })
          .catch(() => {
            if (cancelled) return;
            setCannotCreateRegie(true);
            setLoading(false);
          });
      } else if (hasRegies === true) {
        hasLoadedRegiesRef.current = true;
        // Si utilisateur administrateur, on récupère toutes les régies
        // Sinon, on récupère les régies du régisseur de l'utilisateur
        request
          .get(
            utilisateur.profil.administrateur
              ? getRegies()
              : getUtilisateurRegies(utilisateur.profil.identifiant),
          )
          .then(({ data }: ApiResponseType<RegieType>) => {
            if (cancelled) return;
            const dataFormatted = _.orderBy(data, 'titre');

            // S'il y a des régies dans la BDD mais l'utilisateur n'a accès à aucune régie
            if (_.isEmpty(dataFormatted)) {
              // Si pas administrateur, on affiche un message d'erreur
              if (!utilisateur.profil.administrateur) {
                // Sinon ? Contacter JVS
                setUnknownUser(true);
              }
            } else {
              // Si l'utilisateur a accès à des régies
              setRegies(dataFormatted);

              /** Objet des informations si on est en embedded (encaissement direct) */
              const embeddedInfos = getInfosEncaissementDirect();

              if (embeddedInfos) {
                // En mode embedded (encaissement direct), la régie demandée par le lien
                // prime toujours, y compris sur une régie déjà sélectionnée dans l'onglet
                // ou quand l'utilisateur n'a accès qu'à une seule régie.
                const regieEmbedded = _.find(dataFormatted, {
                  identifiant: embeddedInfos.identifiantRegie,
                });
                if (regieEmbedded) {
                  // On la sélectionne (simple mise à jour si c'est déjà la régie courante)
                  handleRegieSelect(
                    regieEmbedded,
                    regieEmbedded.identifiant === selectedRegieIdentifiant,
                  );
                  // et on redirige sur l'encaissement en direct
                  // sans baseFrontUrl car on est en embedded.
                  // Pas de push si on y est déjà : le provider est remonté à chaque
                  // changement de page, un push sur la même route est inutile.
                  const pathEncaissementDirect = MENUS.LOCATIONS[MENUS.MENU_ENCAISSEMENT_DIRECT];
                  if (history.location?.pathname !== pathEncaissementDirect) {
                    history.push(pathEncaissementDirect);
                  }
                } else {
                  setUnknownUser(true);
                }
              } else if (!_.isNil(selectedRegieIdentifiant)) {
                // Si l'utilisateur était déjà connecté à une régie
                const regieFound = _.find(dataFormatted, { identifiant: selectedRegieIdentifiant });

                // On vérifie qu'il ya toujours accès
                if (regieFound) {
                  // Si c'est le cas, on le repositionne dessus
                  // Et on met à jour les infos de la régie dans le store
                  // avec les infos à jour
                  handleRegieSelect(regieFound, true);
                } else if (_.size(dataFormatted) === 1) {
                  // Sinon s'il a accès à une seule, on la sélectionne
                  handleRegieSelect(_.head(dataFormatted));
                } else {
                  // et sinon ? TODO
                  setUnknownUser(true);
                }
              } else if (_.size(dataFormatted) === 1) {
                // Si une seule, on la sélectionne
                handleRegieSelect(_.head(dataFormatted));
              }
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }

    return () => {
      cancelled = true;
    };
  }, [handleRegieSelect, hasRegies, history, identifiant, selectedRegieIdentifiant, utilisateur]);

  const context = React.useMemo<RegieContextType>(
    () => ({
      regies,
      selectedRegie: selectedRegieIdentifiant,
      showList,
      selectRegie: handleRegieSelect,
    }),
    [handleRegieSelect, regies, selectedRegieIdentifiant, showList],
  );

  const tilesRegies = React.useMemo(
    () =>
      _.map(regies, (regie) => ({
        identifiant: regie.identifiant,
        titre: regie.titre,
        iconName: regie.autonome ? ('ReceiptEuro' as const) : ('FileInput' as const),
        ...((regie.ecole || regie.autonome) && {
          sousTitre: (
            <span
              className="current-color"
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              {regie.ecole && (
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Régie test
                  <Icon
                    name="FlaskConical"
                    className="colored"
                    iconSet="Lucide"
                    style={{ marginLeft: 5 }}
                  />
                </span>
              )}
            </span>
          ),
        }),
      })),
    [regies],
  );

  const sidebarVisible = React.useMemo(
    () => forceSidebarVisible || (hasRegies && !loading && _.isNil(selectedRegieIdentifiant)),
    [forceSidebarVisible, hasRegies, loading, selectedRegieIdentifiant],
  );

  if (cannotCreateRegie) {
    return (
      <Header
        icon
        style={{
          display: 'flex',
          height: '100%',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
        size="huge"
        data-testid="error-message-no-creation-rights"
      >
        <Icon name="info circle" />
        Aucune régie disponible en création
        <br />
        <br />
        <Header.Subheader>
          Veuillez vous rapprocher de votre interlocuteur commercial habituel.
        </Header.Subheader>
      </Header>
    );
  }

  if (unknownUser) {
    return (
      <Header
        icon
        style={{
          display: 'flex',
          height: '100%',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
        size="huge"
        data-testid="error-message"
      >
        <Icon name="bug" />
        Un problème est survenu lors de la récupération des régies qui vous sont affectées
        <br />
        <br />
        <Header.Subheader>Veuillez contacter votre administrateur</Header.Subheader>
      </Header>
    );
  }

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader inverted>Chargement des régies et des droits du régisseur</Loader>
      </Dimmer>
    );
  }

  return (
    <RegieContext.Provider value={context}>
      <SidebarPushable className="regies-sidebar">
        <Sidebar
          data-testid="regies-sidebar"
          animation="overlay"
          icon="labeled"
          visible={sidebarVisible}
          direction="top"
          {...(!_.isNil(selectedRegieIdentifiant) && {
            onHide: () => setForceSidebarVisible(false),
          })}
        >
          <Container
            fluid
            style={{
              justifyContent: 'center',
            }}
          >
            <Segment
              basic
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingTop: '0.5em',
              }}
            >
              <Header as="h3" icon style={{ margin: 0 }}>
                <Header.Content>
                  <Header.Subheader style={{ display: 'inline' }}>
                    Sélection d&apos;une régie
                  </Header.Subheader>
                </Header.Content>
              </Header>
              <TileList
                items={tilesRegies}
                onSelect={(reg) => {
                  const regie = regies?.find((r) => r.identifiant === reg.identifiant);
                  handleRegieSelect(regie);
                }}
                selectedId={selectedRegieIdentifiant}
              />
            </Segment>
          </Container>
        </Sidebar>
        <SidebarPusher dimmed={sidebarVisible}>
          {(!hasRegies || selectedRegieIdentifiant) && children}
        </SidebarPusher>
      </SidebarPushable>
    </RegieContext.Provider>
  );
};

export default RegieContextProvider;
